import { useCallback, useState } from 'react';
import usePlayerData from 'src/hooks/usePlayerData';
import useWatchTime from 'src/hooks/useWatchTime';
import ApplicationService from 'src/services/ApplicationService';
import LiveService from 'src/services/LiveService';
import { valueFromPercentage, calculatePercentage } from 'src/utils/mathUtils';
import { getMouseOrTouchPosition } from 'src/utils/browserUtils';
import { SEEKBAR_PERCENT_INIT } from '../constants';

export type SeekbarEvent = MouseEvent | TouchEvent;
export type ReactSeekbarEvent = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>;
interface IUseSeekbarProps {
  currentPercent: number;
  onSeeking: (percentValue: number) => void;
  onSeekInit?: () => void;
  onSeekEnded?: () => void;
  seekbarDimensions: DOMRect;
}

const MAX_POSITION = 100;

export default function useSeekbar({
  currentPercent,
  onSeeking,
  onSeekInit,
  onSeekEnded,
  seekbarDimensions,
}: IUseSeekbarProps) {
  const [temporaryPercent, setTemporaryPercent] = useState<number>(currentPercent);
  const [seeking, setSeeking] = useState<boolean>(false);
  const [targetTime, setTargetTime] = useState(0);
  const [timePreviewPosition, setTimePreviewPosition] = useState(0);

  const { duration } = useWatchTime();
  const { playerData } = usePlayerData();

  const getSeekbarPositionFrom = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const { left, top, width, height } = seekbarDimensions;
      const eventPosition = getMouseOrTouchPosition(event);

      if (width > height) return eventPosition.x - left;
      return eventPosition.y - top;
    },
    [seekbarDimensions],
  );

  const calcSeekbarPercent = useCallback(
    (event: SeekbarEvent) => {
      const { width, height } = seekbarDimensions;

      const seekbarPercent = calculatePercentage(getSeekbarPositionFrom(event), Math.max(width, height));

      return Math.max(seekbarPercent, SEEKBAR_PERCENT_INIT);
    },
    [seekbarDimensions, getSeekbarPositionFrom],
  );

  const seek = useCallback(
    function (event: SeekbarEvent) {
      const newPosition = calcSeekbarPercent(event);
      setTemporaryPercent(calcSeekbarPercent(event));

      onSeeking(newPosition <= MAX_POSITION ? newPosition : MAX_POSITION);
    },
    [calcSeekbarPercent, onSeeking],
  );

  const endSeeking = useCallback(
    function (event: SeekbarEvent) {
      setSeeking(false);

      onSeekEnded?.();

      if (event instanceof MouseEvent) {
        document.removeEventListener('mousemove', seek);
        document.removeEventListener('mouseup', endSeeking);
      } else {
        document.removeEventListener('touchmove', seek);
        document.removeEventListener('touchend', endSeeking);
      }
    },
    [onSeekEnded, seek],
  );

  const startSeeking = useCallback(
    function (event: ReactSeekbarEvent) {
      onSeekInit?.();

      seek(event.nativeEvent);
      setSeeking(true);

      if (event.nativeEvent instanceof MouseEvent) {
        document.addEventListener('mousemove', seek);
        document.addEventListener('mouseup', endSeeking);
      } else {
        document.addEventListener('touchmove', seek);
        document.addEventListener('touchend', endSeeking);
      }
    },
    [endSeeking, onSeekInit, seek],
  );

  const updateVodTimeTarget = useCallback(
    (event: ReactSeekbarEvent) => {
      const targetTime = valueFromPercentage(calcSeekbarPercent(event.nativeEvent), duration);

      setTimePreviewPosition(getSeekbarPositionFrom(event.nativeEvent));
      setTargetTime(targetTime);
    },
    [calcSeekbarPercent, duration, getSeekbarPositionFrom],
  );

  const updateLiveTimeTarget = useCallback(
    (event: ReactSeekbarEvent) => {
      const liveCurrentTime = (ApplicationService.player as LiveService)?.liveTracker?.liveCurrentTime || 0;
      const targetTime = valueFromPercentage(calcSeekbarPercent(event.nativeEvent), liveCurrentTime);

      setTimePreviewPosition(getSeekbarPositionFrom(event.nativeEvent));
      setTargetTime(targetTime);
    },
    [calcSeekbarPercent, getSeekbarPositionFrom],
  );

  const updateTimeTargetPreview = useCallback(
    (event: ReactSeekbarEvent) => {
      if (playerData?.live) {
        updateLiveTimeTarget(event);
      } else {
        updateVodTimeTarget(event);
      }
    },
    [playerData?.live, updateLiveTimeTarget, updateVodTimeTarget],
  );

  return {
    startSeeking,
    positionPercent: seeking ? temporaryPercent : currentPercent,
    updateTimeTargetPreview,
    targetTime,
    timePreviewPosition,
    seeking,
  };
}
